import { useCallback } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { BatchUploadRejected } from "@/types/marketReport";

type RejectedUploadsModalProps = {
  open: boolean;
  rejected: BatchUploadRejected[];
  onClose: () => void;
  onViewReport: (reportId: string) => void;
};

export function RejectedUploadsModal({
  open,
  rejected,
  onClose,
  onViewReport,
}: RejectedUploadsModalProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {rejected.length} upload
        {rejected.length === 1 ? "" : "s"} skipped
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          These files were not accepted. Accepted files will continue
          processing in the background.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rejected.map((item) => (
            <Box
              key={`${item.filename}-${item.report_id ?? "none"}`}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fafafa",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, wordBreak: "break-word" }}
              >
                {item.filename}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: "#c62828" }}>
                {item.error}
              </Typography>
              {item.status ? (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                >
                  Status: {item.status}
                </Typography>
              ) : null}
              {item.report_id ? (
                <Link
                  component="button"
                  underline="hover"
                  sx={{ mt: 0.75, fontSize: 13 }}
                  onClick={() => onViewReport(item.report_id!)}
                >
                  View existing report
                </Link>
              ) : null}
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
